import {Button} from '@/components/ui/button';
import {Typography} from '@/components/ui/typography';
import {useAnalytics} from '@/hooks/useAnalytics';
import {useEventRegistrationActor} from '@/machines/eventRegistrationMachine/eventRegistrationContext';
import {
  getRegistrationPlatform,
  getSourceAttribution,
} from '@/utils/eventDisplayUtils';
import {CheckCircle2Icon, ExternalLink} from 'lucide-react';
import {FC, ReactNode} from 'react';
import {ActionRow} from '../registrationPanel/actionRow';

interface TrackedConfirmationProps {
  event: {
    id: string;
    sourceUrl: string;
    externalId: string | null;
    eventType: 'route' | 'race' | 'ride';
  };
  actionAccessory?: ReactNode;
}

/**
 * Persistent post-track state for indexed events. Renders the tracked
 * indicator and a button that re-opens the post-track handoff modal so the
 * external registration link stays one click away. Fires
 * `'Handoff Modal Shown'` with `trigger: 'manual'` before dispatching the
 * `OPEN_HANDOFF` event so the analytics call is attributed to the user's
 * explicit action.
 */
export const TrackedConfirmation: FC<TrackedConfirmationProps> = ({
  event,
  actionAccessory,
}) => {
  const actor = useEventRegistrationActor();
  const {track} = useAnalytics();
  const platformDisplay =
    getRegistrationPlatform(event.externalId) ??
    getSourceAttribution(event.sourceUrl)?.name ??
    'the host site';
  const platformAnalytics =
    getRegistrationPlatform(event.externalId) ??
    getSourceAttribution(event.sourceUrl)?.name ??
    null;
  const ctaLabel = `Register on ${platformDisplay}`;

  const handleClick = () => {
    track('Handoff Modal Shown', {
      event_id: event.id,
      event_type: event.eventType,
      platform: platformAnalytics,
      trigger: 'manual',
    });
    actor?.send({type: 'OPEN_HANDOFF'});
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center rounded-full justify-center gap-2 border border-success-300 bg-success-50 text-success-700 py-2">
        <CheckCircle2Icon className="w-4 h-4 stroke-current" />
        <Typography level="B2">Tracked</Typography>
      </div>
      <ActionRow accessory={actionAccessory}>
        <Button variant="outline" className="w-full" onClick={handleClick}>
          <ExternalLink className="w-4 h-4" />
          {ctaLabel}
        </Button>
      </ActionRow>
    </div>
  );
};
